import { useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip,
} from "@mui/material";
import { FaPlus, FaTrashAlt } from "react-icons/fa";
import { MdEdit } from "react-icons/md";
import StatusBadge from "../../generals/StatusBadge";
import LogicContext from "../../Context/LogicContext";
import { subDepartmentService } from "../../services/subDepartment.service";
import SubDepartmentSideScroll from "./SubDepartmentSideScroll";
import SubDepartmentOverview from "./SubDepartmentOverview";
import SubDepartmentFormModal from "./SubDepartmentFormModal";

function isActive(record) {
  return Number(record?.statusId) === 1;
}

function subDepartmentToastMessage(message) {
  return /sub department/i.test(message) ? message : `Sub Department: ${message}`;
}

function SubDepartmentDetailsPage() {
  const [subDepartment, setSubDepartment] = useState(null);
  const [subDepartments, setSubDepartments] = useState([]);
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState("");
  const [formMode, setFormMode] = useState("");
  const [confirmAction, setConfirmAction] = useState(null);
  const [error, setError] = useState("");
  const latestRequest = useRef(0);
  const logicContext = useContext(LogicContext);
  const navigate = useNavigate();
  const { id } = useParams();
  const { pathname } = useLocation();
  const url = pathname.split("/");
  const basePath = `/${url[1]}/${url[2]}/${url[3] || "sub-departments"}`;

  useEffect(() => {
    document.title = "Sub Department Details";
  }, []);

  useEffect(() => {
    if (!id) return undefined;
    const requestId = latestRequest.current + 1;
    latestRequest.current = requestId;
    setLoading(true);
    setError("");
    Promise.all([
      subDepartmentService.getSubDepartment(id),
      subDepartmentService.getSubDepartments(),
    ])
      .then(([detail, list]) => {
        if (latestRequest.current !== requestId) return;
        setSubDepartment(detail);
        setSubDepartments(list.subDepartments);
        setDepartmentOptions(list.departmentOptions);
      })
      .catch((loadError) => {
        if (latestRequest.current !== requestId) return;
        setError(
          loadError?.message
            ? subDepartmentToastMessage(loadError.message)
            : "Unable to load Sub Department details."
        );
        setSubDepartment(null);
      })
      .finally(() => {
        if (latestRequest.current === requestId) setLoading(false);
      });
    return () => {
      latestRequest.current += 1;
    };
  }, [id]);

  function viewSubDepartmentDetails(subDepartmentId) {
    navigate(`${basePath}/${subDepartmentId}`);
  }

  async function refreshAfterWrite() {
    const [detail, list] = await Promise.all([
      subDepartmentService.getSubDepartment(id),
      subDepartmentService.getSubDepartments(),
    ]);
    setSubDepartment(detail);
    setSubDepartments(list.subDepartments);
    setDepartmentOptions(list.departmentOptions);
  }

  async function refreshListAfterWrite() {
    const list = await subDepartmentService.getSubDepartments();
    setSubDepartments(list.subDepartments);
    setDepartmentOptions(list.departmentOptions);
  }

  async function handleEditSubDepartment({ name, statusId, departmentId }) {
    setActionLoading("edit");
    try {
      const result = await subDepartmentService.updateSubDepartment(id, name, statusId, departmentId);
      logicContext.showToast(subDepartmentToastMessage(result.message), "success");
      try {
        await refreshAfterWrite();
      } catch (refreshError) {
        logicContext.showToast(
          subDepartmentToastMessage(
            refreshError?.message || "Unable to refresh Sub Department details."
          ),
          "error"
        );
      }
      setFormMode("");
    } finally {
      setActionLoading("");
    }
  }

  async function handleCreateSubDepartment({ name, departmentId }) {
    setActionLoading("create");
    try {
      const result = await subDepartmentService.createSubDepartment(name, departmentId);
      logicContext.showToast(subDepartmentToastMessage(result.message), "success");
      try {
        await refreshListAfterWrite();
      } catch (refreshError) {
        logicContext.showToast(
          subDepartmentToastMessage(
            refreshError?.message || "Unable to refresh Sub Departments."
          ),
          "error"
        );
      }
      setFormMode("");
    } finally {
      setActionLoading("");
    }
  }

  function handleStatusChange() {
    const action = isActive(subDepartment) ? "deactivate" : "activate";
    setConfirmAction({
      action: "status",
      title: `Confirm ${action} Sub Department`,
      message: `Are you sure you want to ${action} the Sub Department "${subDepartment.name}"?`,
    });
  }

  async function applyStatusChange() {
    setActionLoading("status");
    try {
      const result = await subDepartmentService.setSubDepartmentStatus(
        id,
        isActive(subDepartment) ? 2 : 1
      );
      logicContext.showToast(subDepartmentToastMessage(result.message), "success");
      try {
        await refreshAfterWrite();
      } catch (refreshError) {
        logicContext.showToast(
          subDepartmentToastMessage(
            refreshError?.message || "Unable to refresh Sub Department details."
          ),
          "error"
        );
      }
    } catch (statusError) {
      logicContext.showToast(
        subDepartmentToastMessage(
          statusError?.message || "Unable to update Sub Department status."
        ),
        "error"
      );
    } finally {
      setActionLoading("");
    }
  }

  function handleDeleteSubDepartment() {
    setConfirmAction({
      action: "delete",
      title: "Confirm Delete Sub Department",
      message: `The Sub Department "${subDepartment.name}" will be permanently deleted. Do you want to continue?`,
    });
  }

  async function applyDeleteSubDepartment() {
    setActionLoading("delete");
    try {
      const result = await subDepartmentService.deleteSubDepartment(id);
      logicContext.showToast(subDepartmentToastMessage(result.message), "success");
      try {
        await refreshListAfterWrite();
      } catch (refreshError) {
        logicContext.showToast(
          subDepartmentToastMessage(
            refreshError?.message || "Unable to refresh Sub Departments."
          ),
          "error"
        );
      }
      navigate(basePath);
    } catch (deleteError) {
      logicContext.showToast(
        subDepartmentToastMessage(
          deleteError?.message || "Unable to delete Sub Department."
        ),
        "error"
      );
    } finally {
      setActionLoading("");
    }
  }

  function confirmPendingAction() {
    const action = confirmAction?.action;
    setConfirmAction(null);
    if (action === "status") return applyStatusChange();
    if (action === "delete") return applyDeleteSubDepartment();
  }

  const actionsDisabled = loading || Boolean(actionLoading) || !subDepartment;

  return (
    <div className="@container">
      <div className="mt-2 flex flex-col @xs:flex-row @xs:items-center @xs:justify-end">
        <div className="mr-6 @xs:mt-2 w-fit flex flex-row items-center px-2.5 py-[2px] border-[1px] border-gray-200 bg-white rounded-full">
          <StatusBadge
            statusId={isActive(subDepartment) ? 1 : "I"}
            statusName={subDepartment?.statusName || "-"}
          />
        </div>
        <div className="mt-2 flex flex-row items-center">
          <Tooltip title="Delete Sub Department">
            <button
              onClick={handleDeleteSubDepartment}
              disabled={actionsDisabled}
              className="mr-2 px-3 py-2.5 bg-gray-200 border-[1px] border-gray-300 rounded-md text-white focus:outline-none font-medium hover:bg-gray-100 active:bg-gray-200 transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <FaTrashAlt className="h-[16px] w-[16px] text-gray-600" />
            </button>
          </Tooltip>
          <Tooltip title="Edit Sub Department">
            <button
              onClick={() => setFormMode("edit")}
              disabled={actionsDisabled}
              className="mr-2 px-3 py-2.5 bg-gray-200 border-[1px] border-gray-300 rounded-md text-white focus:outline-none font-medium hover:bg-gray-100 active:bg-gray-200 transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <MdEdit className="h-[16px] w-[16px] text-gray-600" />
            </button>
          </Tooltip>
          <Tooltip title="Add Sub Department">
            <button
              onClick={() => setFormMode("create")}
              disabled={actionsDisabled}
              className="px-3 py-2.5 bg-gray-200 border-[1px] border-gray-300 rounded-md text-white focus:outline-none font-medium hover:bg-gray-100 active:bg-gray-200 transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <FaPlus className="h-[16px] w-[16px] text-gray-600" />
            </button>
          </Tooltip>
        </div>
      </div>

      <div className="mt-2 flex justify-end">
        <button
          onClick={handleStatusChange}
          disabled={actionsDisabled}
          className="px-4 py-2 text-sm rounded-md bg-gray-200 text-gray-700 font-medium hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {isActive(subDepartment) ? "Deactivate Sub Department" : "Activate Sub Department"}
        </button>
      </div>

      <div className="mt-6 w-full flex flex-col @xl:flex-row">
        <div className="my-1 mx-1 w-full @xl:w-[210px] flex-shrink-0 bg-white">
          <SubDepartmentSideScroll
            subDepartments={subDepartments}
            viewSubDepartmentDetails={viewSubDepartmentDetails}
          />
        </div>
        <div className="mt-10 @xl:mt-0 w-full @xl:ml-4">
          <div className="pb-2 border-b-[1.5px] border-b-gray-200 @xl:px-4 flex flex-row flex-wrap @md:justify-between max-w-3xl">
            <div className="mt-2 mr-2 text-sm font-medium cursor-pointer text-primary">
              Sub Department Overview
            </div>
          </div>
          <div className="mt-2 max-auto max-w-6xl text-filterTextColor">
            {loading ? (
              <div className="rounded-lg border border-gray-200 bg-white p-4">
                <div className="h-8 w-1/2 rounded bg-gray-100" />
                <div className="mt-4 h-5 rounded bg-gray-100" />
                <div className="mt-3 h-5 rounded bg-gray-100" />
                <div className="mt-3 h-5 rounded bg-gray-100" />
              </div>
            ) : null}
            {!loading && error ? (
              <div className="rounded-lg border border-red-100 bg-red-50 p-5 text-sm text-red-700">{error}</div>
            ) : null}
            {!loading && !error && !subDepartment ? (
              <div className="rounded-lg border border-gray-200 bg-white p-5 text-sm text-gray-600">
                Sub Department not found.
              </div>
            ) : null}
            {!loading && !error && subDepartment ? (
              <SubDepartmentOverview subDepartment={subDepartment} />
            ) : null}
          </div>
        </div>
      </div>

      {formMode ? (
        <SubDepartmentFormModal
          mode={formMode}
          initialValues={
            formMode === "edit"
              ? {
                  name: subDepartment?.name,
                  departmentId: subDepartment?.departmentId,
                  statusId: subDepartment?.statusId,
                }
              : { name: "", departmentId: "", statusId: 1 }
          }
          departmentOptions={departmentOptions}
          onSubmit={
            formMode === "edit"
              ? handleEditSubDepartment
              : handleCreateSubDepartment
          }
          onClose={() => setFormMode("")}
        />
      ) : null}

      <Dialog
        open={Boolean(confirmAction)}
        onClose={() => setConfirmAction(null)}
        aria-labelledby="sub-department-confirm-title"
      >
        <DialogTitle id="sub-department-confirm-title">{confirmAction?.title}</DialogTitle>
        <DialogContent>{confirmAction?.message}</DialogContent>
        <DialogActions className="px-6 pb-4">
          <Button onClick={() => setConfirmAction(null)}>Cancel Sub Department Action</Button>
          <Button
            variant="contained"
            color={confirmAction?.action === "delete" ? "error" : "primary"}
            onClick={confirmPendingAction}
          >
            Confirm Sub Department Action
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default SubDepartmentDetailsPage;
